import { Box, IconButton, Typography, Modal, FormControlLabel, styled, Switch } from '@mui/material';
import { CloseOutlined } from '@mui/icons-material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import CustomSelect from '../CustomSelect';
import CustomInput from '../CustomInput';
import CustomButton from '../CustomButton';
import CircularLoader from '../shared/CircularLoader/CircularLoader';

const IOSSwitch = styled((props) => (
    <Switch  />
))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        color: '#000',
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#000',
            '& + .MuiSwitch-track': {
                backgroundColor: '#fff',
                opacity: 1,
                border: '1px solid #000',
            },
        },
    },
    '& .MuiSwitch-thumb': {
        width: 22,
        height: 22,
    },
    '& .MuiSwitch-track': {
        borderRadius: 13,
        backgroundColor: '#E9E9EA',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
    },
}));

const ModalForms = ({ open, data, setOpen, name, handleSave, isLoading = false, initialValues: parentInitialValues, formik: parentFormik }) => {
    const validationSchema = Yup.object(
        data.reduce((schema, item) => {
            let validator = Yup.string(); 
            if (item.key === 'email') {
                validator = Yup.string().email(`Enter a valid ${item.label}`);
            } else if (item.key === 'phone' || item.label.toLowerCase().includes('phone')) {
                validator = Yup.string().matches(/^\d+$/, `Enter a valid ${item.label}`);
            } else if (item.type === 'toggle') {
                validator = Yup.boolean();
            }
            if (item.minLength) {
                validator = validator.min(item.minLength, `${item.label} must be at least ${item.minLength} characters`);
            }
            if (item.maxLength) {
                validator = validator.max(item.maxLength, `${item.label} must be at most ${item.maxLength} characters`);
            }
            if (item.required) {
                validator = validator.required(`${item.label} is required`);
            }
            schema[item.key] = validator;
            return schema;
        }, {})
    );

    const defaultInitialValues = data.reduce((values, item) => {
        values[item.key] = item.type === 'toggle' ? false : '';
        return values;
    }, {});

    const formikProps = parentFormik || {
        initialValues: parentInitialValues || defaultInitialValues,
        validationSchema,
        onSubmit: handleSave || (() => {}),
        enableReinitialize: true,
    };
    
    if (isLoading) {
        return <CircularLoader />;
    }

    return (
        <Modal open={open} sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%" }}>
            <Formik {...formikProps}>
                {({ values, errors, touched, setFieldValue }) => (
                    <Form style={{ maxWidth: '90%' }}>
                        <Box
                            sx={{
                                padding: 4,
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                                boxShadow: 3,
                                maxWidth: '90%',
                                mx: 'auto',
                            }}
                        >
                            {/* Title */}
                            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                                <Typography variant="h6" fontWeight="bold" gutterBottom>
                                    {name}
                                </Typography>
                                <IconButton onClick={() => setOpen(false)}>
                                    <CloseOutlined />
                                </IconButton>
                            </Box>

                            {/* Form Fields */}
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", maxHeight: '400px', paddingY: 2, overflowY: 'auto', scrollbarWidth: 'none' }}>
                                {data.map((item) => (
                                    <Box key={item.key} sx={{ minWidth: item?.width }}>
                                        {item.type === "select" ? (
                                            <CustomSelect
                                                label={item.label}
                                                required={item.required}
                                                data={item.data || [`${item.label} 1`, `${item.label} 2`, `${item.label} 3`]}
                                                value={values[item.key]}
                                                handleChange={(e) => setFieldValue(item.key, e.target.value)}
                                                error={touched[item.key] && Boolean(errors[item.key])}
                                                helperText={touched[item.key] && errors[item.key]}
                                                noDataMessage="No data"
                                            />
                                        ) : item.type === "toggle" ? (
                                            <FormControlLabel
                                                control={
                                                    <IOSSwitch
                                                        checked={values[item.key]}
                                                        onChange={(e) => setFieldValue(item.key, e.target.checked)}
                                                    />
                                                }
                                                label={item.label}
                                            />
                                        ) : (
                                            <CustomInput
                                                label={item.label}
                                                required={item.required}
                                                value={values[item.key]}
                                                onChange={(e) => setFieldValue(item.key, e.target.value)}
                                                onBlur={() => setFieldValue(item.key, values[item.key])}
                                                error={touched[item.key] && Boolean(errors[item.key])}
                                                helperText={touched[item.key] && errors[item.key]}
                                                height="42px"
                                            />
                                        )}
                                    </Box>
                                ))}
                            </Box>

                            {/* Actions */}
                            <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
                                <CustomButton
                                    text={isLoading ? "Saving..." : "Save"}
                                    color="black"
                                    bgcolor="#e0e7ff"
                                    border="#3D52A0"
                                    type="submit"
                                    disabled={isLoading}
                                />
                                <CustomButton
                                    text="Cancel"
                                    color="black"
                                    bgcolor="white"
                                    border="black"
                                    onClick={() => setOpen(false)}
                                    disabled={isLoading}
                                />
                            </Box>
                        </Box>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
};

export default ModalForms;
